import { forwardRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { topicLabel } from '@gogrov/shared'
import type { Headline } from '@/types/database'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { noDashes } from '@/lib/text'

export interface HeadlineCardProps {
  headline: Headline
  className?: string
  style?: CSSProperties
  /** Which corner is lit and how strongly, from the drag. */
  glow?: { dir: 'left' | 'right' | null; t: number }
  onPointerDown?: (e: ReactPointerEvent<HTMLElement>) => void
}

/**
 * One headline, as a card you could pick up.
 *
 * Topic pill at the top, the headline, then the lead image: the photo that ran
 * with the source article, credited to the outlet, or the topic's own glyph
 * when there is none. The source line is the one interactive thing inside a
 * drag target, so it stops its own pointer events from starting a swipe.
 */
export const HeadlineCard = forwardRef<HTMLElement, HeadlineCardProps>(function HeadlineCard(
  { headline, className, style, glow, onPointerDown }, ref,
) {
  const [imgFailed, setImgFailed] = useState(false)
  const source = headline.source_name ?? 'Local source'
  const hits = headline.keyword_hits ?? []
  const showImage = !!headline.image_url && !imgFailed
  const t = glow?.t ?? 0

  const corner = (side: 'left' | 'right'): CSSProperties => {
    const on = glow?.dir === side
    const rgb = side === 'left' ? 'var(--pass)' : 'var(--build)'
    if (!on || t === 0) return {}
    return {
      background: `rgba(${rgb}, ${0.28 + 0.52 * t})`,
      filter: `drop-shadow(0 0 ${10 + 20 * t}px rgba(${rgb}, ${0.45 + 0.45 * t}))`,
      opacity: 1,
    }
  }

  return (
    <article ref={ref} className={`hcard ${className ?? ''}`} style={style} onPointerDown={onPointerDown}>
      <span className="hcard__corner hcard__corner--left" style={corner('left')} aria-hidden="true">
        <Chevron dir="left" />
      </span>
      <span className="hcard__corner hcard__corner--right" style={corner('right')} aria-hidden="true">
        <Chevron dir="right" />
      </span>

      {headline.topic && (
        <span className="hcard__pill">
          <TopicGlyph topic={headline.topic} size={22} />
          {topicLabel(headline.topic)}
        </span>
      )}

      <h2 className="hcard__headline">{highlight(noDashes(headline.headline), hits)}</h2>

      <div className="hcard__badges">
        {headline.coverage === 'original' && (
          <span className="gg-badge gg-badge--original" title="From a primary record no outlet has covered yet">Original</span>
        )}
        {headline.coverage === 'corroborated' && (
          <span className="gg-badge gg-badge--record" title="From a primary record, also covered by the news">Corroborated</span>
        )}
        {/* A keyword that is only the place name adds nothing next to the place tag. */}
        {(() => {
          const hit = hits.find((h) => h.toLowerCase() !== (headline.place ?? '').toLowerCase())
          return hit ? <span className="gg-badge gg-badge--hit" title="Matches one of your keywords">{hit}</span> : null
        })()}
        {headline.place && <span className="gg-badge">{headline.place}</span>}
        {headline.layer === 'national' && !headline.place && <span className="gg-badge">National</span>}
      </div>

      <div className={`hcard__media${showImage ? '' : ' hcard__media--glyph'}`}>
        {showImage ? (
          <img src={headline.image_url!} alt="" draggable={false} loading="lazy" referrerPolicy="no-referrer" onError={() => setImgFailed(true)} />
        ) : (
          <>
            <TopicGlyph topic={headline.topic} size={84} />
            {headline.summary && <p className="hcard__summary">{noDashes(headline.summary)}</p>}
          </>
        )}
        {showImage && headline.image_credit && <span className="hcard__credit">Photo: {headline.image_credit}</span>}
      </div>

      <div className="hcard__foot">
        {headline.source_url ? (
          <a
            className="hcard__source"
            href={headline.source_url}
            target="_blank"
            rel="noreferrer noopener"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            title={`Open this story on ${source}`}
          >
            {source}
          </a>
        ) : (
          <span className="hcard__source">{source}</span>
        )}
        {headline.provenance === 'record' && <span className="hcard__kind">Public record</span>}
      </div>
    </article>
  )
})

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </svg>
  )
}

/** Marks the user's keywords inside the headline. */
function highlight(text: string, hits: string[]): ReactNode {
  if (!hits.length) return text
  const escaped = hits.filter(Boolean).map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  if (!escaped.length) return text
  const re = new RegExp(`(${escaped.join('|')})`, 'gi')
  return text.split(re).map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part))
}
